import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import BaseCard from './BaseCard';
import { PEANUT_BUTTER_ORANGE_LIGHT } from '../../../dashboardColors';

// Custom tooltip component with "Count:" label in black
const CustomTooltip = ({ active, payload, label, t }) => {
  if (active && payload && payload.length) {
    const value = typeof payload[0].value === 'number' ? Math.round(payload[0].value) : payload[0].value;
    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #ccc',
        padding: '8px 12px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 'bold'
      }}>
        <div style={{ marginBottom: 4 }}>{label}</div>
        <div style={{ color: '#000000' }}>
          {t('count')}: {value}
        </div>
      </div>
    );
  }
  return null;
};

const AverageTimeCard = ({ title, icon, data, t, barColor = PEANUT_BUTTER_ORANGE_LIGHT }) => {
  // Calculate if we're past mid-quarter (we only show current quarter after mid-quarter)
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1-12
  const currentQuarter = Math.ceil(currentMonth / 3);

  // Get the month within the quarter (1, 2, or 3)
  const monthInQuarter = ((currentMonth - 1) % 3) + 1;
  // Get day of month
  const dayOfMonth = currentDate.getDate();

  // Mid-quarter is approximately day 15 of month 2 (middle month of quarter)
  // Q1: mid-Feb (month 2), Q2: mid-May (month 5), Q3: mid-Aug (month 8), Q4: mid-Nov (month 11)
  const isPastMidQuarter = monthInQuarter > 2 || (monthInQuarter === 2 && dayOfMonth >= 15);

  // Determine the "most recent quarter to show"
  // If past mid-quarter, show current quarter; otherwise show previous quarter
  let mostRecentYear = currentYear;
  let mostRecentQuarter = currentQuarter;

  if (!isPastMidQuarter) {
    // Go back one quarter
    mostRecentQuarter--;
    if (mostRecentQuarter < 1) {
      mostRecentQuarter = 4;
      mostRecentYear--;
    }
  }

  const existingData = data || [];

  // If we have actual data, use the most recent from data if it's earlier than our calculated max
  if (existingData.length > 0) {
    const lastItem = existingData[existingData.length - 1];
    if (lastItem?.quarter) {
      const match = lastItem.quarter.match(/(\d{4})\s*Q(\d)/);
      if (match) {
        const dataYear = parseInt(match[1]);
        const dataQuarter = parseInt(match[2]);
        // Use data's most recent if it's before our calculated max
        if (dataYear < mostRecentYear || (dataYear === mostRecentYear && dataQuarter <= mostRecentQuarter)) {
          mostRecentYear = dataYear;
          mostRecentQuarter = dataQuarter;
        }
      }
    }
  }

  // Create array of 4 quarters ending with the most recent quarter
  const chartData = [];
  let year = mostRecentYear;
  let quarter = mostRecentQuarter;

  // Go back 3 quarters to get 4 total
  for (let i = 0; i < 3; i++) {
    quarter--;
    if (quarter < 1) {
      quarter = 4;
      year--;
    }
  }

  // Now add 4 quarters starting from that point
  for (let i = 0; i < 4; i++) {
    const quarterKey = `${year} Q${quarter}`;
    const existingItem = existingData.find(d => d.quarter === quarterKey);
    chartData.push({
      quarter: quarterKey,
      value: existingItem && typeof existingItem.value === 'number' && isFinite(existingItem.value)
        ? Math.round(existingItem.value)
        : 0
    });
    quarter++;
    if (quarter > 4) {
      quarter = 1;
      year++;
    }
  }

  const maxValue = chartData.length > 0
    ? Math.max(...chartData.map(d => d.value || 0), 12)
    : 12;

  // Y-axis tick formatter to show whole numbers
  const formatYAxisTick = (value) => {
    return Math.round(value);
  };

  return (
    <BaseCard title={title} icon={icon}>
      <Box display="flex" flexDirection="column" height="100%">
        {existingData.length > 0 ? (
          <Box flex={1} minHeight={160} mt={1}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }} barSize={70}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="quarter" tick={{ fontSize: 12, fontWeight: 'bold' }} />
                <YAxis domain={[0, maxValue]} tick={{ fontSize: 12, fontWeight: 'bold' }} tickFormatter={formatYAxisTick} />
                <Tooltip content={<CustomTooltip t={t} />} />
                <Bar dataKey="value" fill={barColor} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box height={160} display="flex" alignItems="center" justifyContent="center" mt={1}>
            <Typography color="textSecondary" variant="body2">
              {t('no_data_available')}
            </Typography>
          </Box>
        )}
      </Box>
    </BaseCard>
  );
};

AverageTimeCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
  data: PropTypes.arrayOf(PropTypes.shape({
    quarter: PropTypes.string,
    value: PropTypes.number
  })),
  t: PropTypes.func.isRequired,
  barColor: PropTypes.string
};

export default AverageTimeCard;
